import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { Mutations } from '@shared/api';
import {
  toDeletedCard,
  useLocalWorkflowEntityChanges,
} from '@shared/data/cards/hooks/LocalWorkflowEntityChanges';
import { API } from '@shared/types/api';

type DeleteEntitiesFunction = (ids: string[]) => Promise<true>;

export function useDeleteEntitiesMutation(workflowTemplateId: string): DeleteEntitiesFunction {
  const [deleteWorkflowEntity] = useMutation<
    API.DeleteWorkflowEntityMutation,
    API.DeleteWorkflowEntityMutationVariables
  >(Mutations.entity.deleteWorkflowEntity);
  const [deleteWorkflowEntities] = useMutation<
    API.DeleteWorkflowEntitiesMutation,
    API.DeleteWorkflowEntitiesMutationVariables
  >(Mutations.entity.deleteWorkflowEntities);
  const { readCards, publish } = useLocalWorkflowEntityChanges();

  return useCallback(
    async (ids: string[]) => {
      // only the cards this client has can be taken out of its lists now; the rest wait for the event
      const previousEntities = readCards(ids);
      const publishDeleted = () => {
        const deletedAt = new Date().toISOString();
        publish({
          type: 'WORKFLOW_ENTITY_DELETED',
          entities: previousEntities.map(card => toDeletedCard(card, deletedAt)),
          previousEntities,
        });
      };
      if (ids.length === 1) {
        const { data } = await deleteWorkflowEntity({
          variables: {
            input: {
              workflowTemplateId,
              id: ids[0],
            },
          },
        });
        const success = data?.deleteWorkflowEntity;
        if (success) {
          publishDeleted();
          return success;
        }
        throw new Error('Failed to delete entity');
      } else {
        const { data } = await deleteWorkflowEntities({
          variables: {
            input: {
              workflowTemplateId,
              ids,
            },
          },
        });

        const success = data?.deleteWorkflowEntities;

        if (success) {
          publishDeleted();
          return success;
        } else {
          throw new Error('Failed to delete entities');
        }
      }
    },
    [workflowTemplateId, deleteWorkflowEntity, deleteWorkflowEntities, readCards, publish],
  );
}
